/**
 * API 配置 — Single Source of Truth
 *
 * 全局唯一的 API URL 定义，其它模块（llmService / createHttpClient 等）
 * 均通过 import 引用本文件常量，禁止自行定义 API_BASE_URL。
 *
 * @module shared/config
 */

/**
 * 规范化 VITE_API_HOST：去掉尾部斜杠；若误写成 `.../api/v2` 则剥掉，避免 BASE_URL 变成 `.../api/v2/api/v2`。
 */
function normalizeApiHost(raw: string): string {
  let h = (raw || "").trim().replace(/\/+$/, "");
  h = h.replace(/\/api\/v2$/i, "").replace(/\/+$/, "");
  return h;
}

/**
 * 后端服务地址（主机+端口，不含 /api/v2）
 * 开发环境 fallback 为空串 → 走 Vite devServer.proxy (/api → 127.0.0.1:8000)
 * 生产环境 通过 VITE_API_HOST 注入实际地址
 */
export const API_HOST: string = normalizeApiHost(
  String(import.meta.env.VITE_API_HOST || ""),
);

/**
 * 带版本号的 API 基础 URL（用于 axios baseURL）
 * 示例: /api/v2  或  http://10.0.0.1:8000/api/v2
 */
export const API_BASE_URL: string = API_HOST ? `${API_HOST}/api/v2` : "/api/v2";

/**
 * 不带版本号的 API 根路径（用于需要自行拼接版本前缀的服务，如 LLM）
 * 示例: /api  或  http://10.0.0.1:8000/api
 */
export const API_ROOT_URL: string = API_HOST ? `${API_HOST}/api` : "/api";

/**
 * 普通 API 超时时间（毫秒）
 */
export const API_TIMEOUT = 30000;

/**
 * LLM / AI 分析类端点超时时间（毫秒）
 * 此类请求涉及大模型推理，耗时可达 60-120 秒
 */
export const LLM_TIMEOUT = 120000;

/**
 * 匹配需要使用 LLM_TIMEOUT 的端点路径片段
 */
export const LLM_ENDPOINT_PATTERNS = [
  "/chronic-care/clinical/",
  "/record-check/",
  "/mcp/",
  "/ai-chat/",
  "/ai/",
  "/cdss/",
  "/temporal-analysis/",
  "/tcm-review/",
  "/guideline/",
  "/clinical-summary/",
  "/differential-diagnosis/",
  "/tcm-diagnosis/",
  "/medical-orders/ai-suggestions",
] as const;

/**
 * API 端点配置
 */
export const API_ENDPOINTS = {
  // 病历相关
  MEDICAL_RECORDS: "/medical-records",

  // 患者相关
  PATIENTS: "/patients",

  // AI 相关
  AI_CHAT: "/ai/chat",
  AI_STREAM: "/ai/stream",

  // 认证相关
  AUTH_LOGIN: "/auth/login",
  AUTH_LOGOUT: "/auth/logout",
  AUTH_USER: "/auth/user",
} as const;

/**
 * 获取完整 API URL
 * @param endpoint - API 端点
 * @returns 完整 URL
 */
export function getApiUrl(endpoint: string): string {
  return `${API_BASE_URL}${endpoint}`;
}
